<script setup lang="ts">
import { computed, ref } from "vue";
import { useAppStore } from "@/store/modules/app";
import { useUserStore } from "@/store/modules/user";
import { useThemeStore } from "@/store/modules/theme";
import { createSubscriptionTransaction } from "@/service/api/billing";
import GlobalLogo from "../global-logo/index.vue";
import TierUpgradeButton from "@/components/common/tier-upgrade-button.vue";
import TierUpgradeModal from "@/components/common/tier-upgrade-modal.vue";

defineOptions({
    name: "GlobalHeader",
});

interface Props {
    /** Whether to show the logo */
    showLogo?: App.Global.HeaderProps["showLogo"];
    /** Whether to show the menu toggler */
    showMenuToggler?: App.Global.HeaderProps["showMenuToggler"];
}

defineProps<Props>();

const appStore = useAppStore();
const userStore = useUserStore();

const themeStore = useThemeStore();
const rawTier = computed(() => userStore.getApiTier());
const exchangeRate = computed(() => appStore.exchangeRate);

// Tier upgrade functionality
const showTierUpgradeModal = ref(false);
const loadingUpgrade = ref(false);

const userTierNumber = computed(() => {
    const lastChar = rawTier.value.slice(-1) || -1;
    return Number(lastChar);
});

const totalPaidInUnit = computed(() => userStore.getTotalPaidInUnit());

const handleOpenTierModal = () => {
    showTierUpgradeModal.value = true;
};

const handleCloseTierModal = () => {
    showTierUpgradeModal.value = false;
};

const handleTierUpgrade = async (requiredAmount: number) => {
    try {
        loadingUpgrade.value = true;

        // Convert UNIT to IRT
        const irtAmount = Math.ceil(requiredAmount * exchangeRate.value);

        // Create transaction using existing billing system
        const response = await createSubscriptionTransaction("api", irtAmount);

        if (response?.data?.encoded_details) {
            const encodedDetails = encodeURIComponent(
                response.data.encoded_details
            );
            const paymentUrl = `http://pay.avalai.ir/c?details=${encodedDetails}`;

            // Open payment gateway in new tab
            window.open(paymentUrl, "_blank");
        }
    } catch (error) {
        console.error("Tier upgrade payment error:", error);
        window.$message?.error(
            "Failed to create payment transaction. Please try again."
        );
    } finally {
        loadingUpgrade.value = false;

        // Close modal after opening payment
        showTierUpgradeModal.value = false;
    }
};
</script>

<template>
    <DarkModeContainer class="h-full flex-y-center shadow-header overflow-y-auto">
        <GlobalLogo v-if="showLogo" :show-title="!appStore.siderCollapse" :direction="appStore.direction" class="h-full"
            :style="{ width: themeStore.sider.width + 'px' }" />
        <!-- Moved MenuToggler to the right side -->
        <div class="w-full flex justify-start">
            <!-- Wrapped MenuToggler in a div with flex justify-end to align it right -->
            <MenuToggler v-if="showMenuToggler" :collapsed="appStore.siderCollapse" :direction="appStore.direction"
                @click="appStore.toggleSiderCollapse" />
        </div>
        <!-- Moved this div to the left side -->
        <div class="h-full flex-y-center justify-start px-2 gap-1" style="flex-grow: 1">
            <!-- Tier Upgrade Button -->
            <TierUpgradeButton :current-tier="rawTier" :user-tier-number="userTierNumber" :show-upgrade="false" :is-mobile="appStore.isMobile"
                :direction="appStore.direction" @open-modal="handleOpenTierModal" />
            <!-- Adjusted justify to justify-end and added flex-grow -->
            <CurrentExchange v-if="rawTier !== 'zero'" :lang="appStore.locale" :current-exchange-rate="exchangeRate"
                :direction="appStore.direction" />

            <ThemeSchemaSwitch :theme-schema="themeStore.themeScheme" :is-dark="themeStore.darkMode"
                @switch="themeStore.toggleThemeScheme" />

            <LangSwitch :lang="appStore.locale" :lang-options="appStore.localeOptions"
                @change-lang="appStore.changeLocale" />
            <!-- <UserAvatar /> -->
        </div>

        <!-- Tier Upgrade Modal -->
        <TierUpgradeModal v-model:visible="showTierUpgradeModal" :current-tier="rawTier"
            :total-paid-in-unit="totalPaidInUnit" :exchange-rate="exchangeRate" :is-mobile="appStore.isMobile"
            :direction="appStore.direction" :loading="loadingUpgrade" @upgrade="handleTierUpgrade"
            @close="handleCloseTierModal" />
    </DarkModeContainer>
</template>

<style scoped></style>
